import React, { useState, useEffect } from 'react';
import FolderService from './api/Folder.service';
import DocumentService from './api/Document.service';
import  {getFileUrlPrefix} from './FileUtils';

// Modal Component (remains the same)
const Modal = ({ children, title, onClose }) => {
  return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">{title}</h2>
          <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl"
              aria-label="Close"
          >
            &times;
          </button>
          {children}
        </div>
      </div>
  );
};

const App = () => {
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showUploadDocumentModal, setShowUploadDocumentModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newDocumentFile, setNewDocumentFile] = useState(null);
  const [message, setMessage] = useState(''); // For success/error messages

  // Effect to load folders on initial mount
  useEffect(() => {
    const loadFolders = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchedFolders = await FolderService.fetchFolders(); // Using FolderService
        setFolders(fetchedFolders);
        if (fetchedFolders.length > 0) {
          setCurrentFolder(fetchedFolders[0]); // Select the first folder by default
        }
      } catch (err) {
        setError('Failed to load folders.');
        console.error('Error fetching folders:', err);
      } finally {
        setLoading(false);
      }
    };
    loadFolders();
  }, []);

  // Effect to load documents when currentFolder changes
  useEffect(() => {
    const loadDocuments = async () => {
      if (currentFolder) {
        setLoading(true);
        setError(null);
        try {
          const fetchedDocuments = await DocumentService.fetchDocuments(currentFolder.id, currentFolder.fullPath); // Using DocumentService
          setDocuments(fetchedDocuments);
        } catch (err) {
          setError('Failed to load documents.');
          console.error('Error fetching documents:', err);
        } finally {
          setLoading(false);
        }
      } else {
        setDocuments([]); // Clear documents if no folder is selected
      }
    };
    loadDocuments();
  }, [currentFolder]);

  const handleFolderClick = (folder) => {
    setCurrentFolder(folder);
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) {
      setMessage('Folder name cannot be empty.');
      return;
    }
    setLoading(true);
    setError(null);
    setMessage('');
    try {
      const createdFolder = await FolderService.createFolder(newFolderName); // Using FolderService
      setFolders((prevFolders) => [...prevFolders, createdFolder]);
      setNewFolderName('');
      setShowCreateFolderModal(false);
      setMessage('Folder created successfully!');
    } catch (err) {
      setError('Failed to create folder.');
      setMessage(`Failed to create folder: ${err.message || 'Unknown error'}`);
      console.error('Error creating folder:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!currentFolder) {
      setMessage('Please select a folder to upload a document.');
      return;
    }
    if (!newDocumentFile) {
      setMessage('Please select a file to upload.');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage('');
    try {
      const uploadedDocument = await DocumentService.uploadDocument(currentFolder.fullPath, newDocumentFile); // Using DocumentService
      setDocuments((prevDocuments) => [...prevDocuments, uploadedDocument]);
      setNewDocumentFile(null);
      setShowUploadDocumentModal(false);
      setMessage('Document uploaded successfully!');
    } catch (err) {
      setError('Failed to upload document.');
      setMessage(`Failed to upload document: ${err.message || 'Unknown error'}`);
      console.error('Error uploading document:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
      <div className="min-h-screen bg-gray-100 font-sans text-gray-900 flex flex-col">
        {/* Header */}
        <header className="bg-white shadow-md p-4 flex items-center justify-between">
          <h1 className="text-3xl font-extrabold text-blue-600">File Manager</h1>
          <div className="flex space-x-4">
            <button
                onClick={() => setShowCreateFolderModal(true)}
                className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
            >
              Create Folder
            </button>
            <button
                onClick={() => setShowUploadDocumentModal(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
            >
              Upload Document
            </button>
          </div>
        </header>

        {/* Main Content Area */}
        <div className="flex flex-1 overflow-hidden">
          {/* Folder Sidebar */}
          <aside className="w-64 bg-white border-r border-gray-200 p-4 overflow-y-auto shadow-lg">
            <h2 className="text-xl font-bold mb-4 text-gray-700">Folders</h2>
            {loading && <p className="text-gray-500">Loading folders...</p>}
            {error && <p className="text-red-500">{error}</p>}
            <ul className="space-y-2">
              {folders.map((folder) => (
                  <li key={folder.id}>
                    <button
                        onClick={() => handleFolderClick(folder)}
                        className={`flex items-center w-full text-left p-3 rounded-lg transition duration-200 ease-in-out
                    ${currentFolder && currentFolder.id === folder.id
                            ? 'bg-blue-100 text-blue-700 font-semibold shadow-inner'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
                        }`}
                    >
                      <svg className="w-5 h-5 mr-3 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"></path>
                      </svg>
                      {folder.name}
                    </button>
                  </li>
              ))}
            </ul>
          </aside>

          {/* Document View */}
          <main className="flex-1 p-6 overflow-y-auto bg-gray-50">
            {message && (
                <div className={`p-3 mb-4 rounded-lg ${error ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  {message}
                </div>
            )}
            {currentFolder ? (
                <>
                  <h2 className="text-2xl font-bold mb-6 text-gray-800">
                    Documents in: <span className="text-blue-600">{currentFolder.name}</span>
                  </h2>
                  {loading && <p className="text-gray-500">Loading documents...</p>}
                  {error && <p className="text-red-500">{error}</p>}
                  {documents.length === 0 && !loading && !error && (
                      <p className="text-gray-500">No documents in this folder. Upload one!</p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {documents.map((doc, index) => (
                        <div
                            key={index}
                            className="bg-white rounded-lg shadow-md p-4 flex flex-col items-center justify-center text-center transition duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg"
                        >
                          <svg className="w-12 h-12 text-gray-400 mb-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0113 3.414L16.586 7A2 2 0 0117 8.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm0 3a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1z" clipRule="evenodd"></path>
                          </svg>
                          <p className="font-medium text-gray-700 truncate w-full px-2">{doc.fileName}</p>
                          {/*<p className="text-sm text-gray-500">{(doc.size / 1024).toFixed(2)} KB</p>*/}
                          {/* For demonstration, you might link to the document URL */}
                          {doc.filePath && (
                              <a href={getFileUrlPrefix() +doc.filePath} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline text-sm mt-2">
                                View
                              </a>
                          )}
                        </div>
                    ))}
                  </div>
                </>
            ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 text-xl">
                  <svg className="w-16 h-16 mb-4 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"></path>
                  </svg>
                  <p>Select a folder to view its contents.</p>
                </div>
            )}
          </main>
        </div>

        {/* Create Folder Modal */}
        {showCreateFolderModal && (
            <Modal title="Create New Folder" onClose={() => setShowCreateFolderModal(false)}>
              <form onSubmit={handleCreateFolder}>
                <div className="mb-4">
                  <label htmlFor="folderName" className="block text-gray-700 text-sm font-bold mb-2">
                    Folder Name:
                  </label>
                  <input
                      type="text"
                      id="folderName"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter folder name"
                      required
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                      type="button"
                      onClick={() => setShowCreateFolderModal(false)}
                      className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition duration-300 ease-in-out"
                  >
                    Cancel
                  </button>
                  <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out"
                      disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </form>
            </Modal>
        )}

        {/* Upload Document Modal */}
        {showUploadDocumentModal && (
            <Modal title="Upload Document" onClose={() => setShowUploadDocumentModal(false)}>
              <form onSubmit={handleUploadDocument}>
                <div className="mb-4">
                  <label htmlFor="documentFile" className="block text-gray-700 text-sm font-bold mb-2">
                    Select Document:
                  </label>
                  <input
                      type="file"
                      id="documentFile"
                      onChange={(e) => setNewDocumentFile(e.target.files[0])}
                      className="block w-full text-sm text-gray-700
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
                      required
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                      type="button"
                      onClick={() => setShowUploadDocumentModal(false)}
                      className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition duration-300 ease-in-out"
                  >
                    Cancel
                  </button>
                  <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out"
                      disabled={loading}
                  >
                    {loading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </form>
            </Modal>
        )}
      </div>
  );
};

export default App;
